import { useState } from 'react'
import { Plus, Edit2, Trash2, X, Activity, Database, AlertTriangle, CheckCircle } from 'lucide-react'

export default function AuditLoggingPage() {
  const [logs, setLogs] = useState([
    {
      id: 1,
      name: 'System Access Audit Log',
      dataSource: 'system',
      retentionDays: 365,
      status: 'active',
      logSize: '12.5 GB',
      lastRotation: new Date().toLocaleDateString(),
      entries: 5200000
    },
    {
      id: 2,
      name: 'API Activity Log',
      dataSource: 'api',
      retentionDays: 90,
      status: 'active',
      logSize: '4.2 GB',
      lastRotation: new Date().toLocaleDateString(),
      entries: 2100000
    },
    {
      id: 3,
      name: 'Security Event Log',
      dataSource: 'security',
      retentionDays: 730,
      status: 'active',
      logSize: '18.7 GB',
      lastRotation: new Date().toLocaleDateString(),
      entries: 850000
    }
  ])

  const [showModal, setShowModal] = useState(false)
  const [editingLog, setEditingLog] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    dataSource: 'system',
    retentionDays: 90
  })

  const handleCreateClick = () => {
    setEditingLog(null)
    setFormData({
      name: '',
      dataSource: 'system',
      retentionDays: 90
    })
    setShowModal(true)
  }

  const handleEditLog = (log) => {
    setEditingLog(log)
    setFormData({
      name: log.name,
      dataSource: log.dataSource,
      retentionDays: log.retentionDays
    })
    setShowModal(true)
  }

  const handleSaveLog = () => {
    if (!formData.name) {
      alert('Please fill in all required fields')
      return
    }

    if (editingLog) {
      setLogs(logs.map(l =>
        l.id === editingLog.id
          ? { ...l, ...formData, lastRotation: new Date().toLocaleDateString() }
          : l
      ))
    } else {
      const newLog = {
        id: Date.now(),
        ...formData,
        status: 'active',
        logSize: '0 GB',
        lastRotation: new Date().toLocaleDateString(),
        entries: 0
      }
      setLogs([...logs, newLog])
    }

    setShowModal(false)
  }

  const handleDeleteLog = (id) => {
    if (confirm('Are you sure you want to delete this log configuration?')) {
      setLogs(logs.filter(l => l.id !== id))
    }
  }

  const getSourceColor = (source) => {
    switch (source) {
      case 'system': return 'bg-blue-600/20 text-blue-400'
      case 'api': return 'bg-cyan-600/20 text-cyan-400'
      case 'security': return 'bg-red-600/20 text-red-400'
      case 'user': return 'bg-green-600/20 text-green-400'
      default: return 'bg-slate-600/20 text-slate-400'
    }
  }

  const getTotalLogSize = () => {
    return logs.reduce((sum, l) => {
      const size = parseFloat(l.logSize)
      return sum + size
    }, 0).toFixed(1)
  }

  const getTotalEntries = () => {
    return logs.reduce((sum, l) => sum + l.entries, 0).toLocaleString()
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Activity className="w-8 h-8 text-blue-400" />
            <h1 className="text-3xl font-bold text-white">Audit & Logging</h1>
          </div>
          <p className="text-slate-400">Configure and monitor system audit logs and activity records</p>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Active Logs</div>
            <div className="text-3xl font-bold text-blue-400">{logs.length}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Log Size</div>
            <div className="text-3xl font-bold text-cyan-400">{getTotalLogSize()} GB</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Entries</div>
            <div className="text-3xl font-bold text-green-400">{getTotalEntries()}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Avg Retention</div>
            <div className="text-3xl font-bold text-yellow-400">{Math.round(logs.reduce((sum, l) => sum + l.retentionDays, 0) / logs.length)} days</div>
          </div>
        </div>

        {/* Create Button */}
        <button
          onClick={handleCreateClick}
          className="mb-6 flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold rounded-lg transition-all"
        >
          <Plus className="w-5 h-5" />
          Create Log Configuration
        </button>

        {/* Logs List */}
        <div className="space-y-4">
          {logs.map((log) => (
            <div
              key={log.id}
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Database className="w-5 h-5 text-blue-400" />
                    <h3 className="text-lg font-semibold text-white">{log.name}</h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-bold ${getSourceColor(log.dataSource)}`}>
                      {log.dataSource.toUpperCase()}
                    </span>
                    {log.status === 'active' && <CheckCircle className="w-5 h-5 text-green-400" />}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm mb-4">
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500 block text-xs">Log Size</span>
                      <span className="text-white font-semibold mt-1">{log.logSize}</span>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500 block text-xs">Log Entries</span>
                      <span className="text-white font-semibold mt-1">{(log.entries / 1000000).toFixed(1)}M</span>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500 block text-xs">Retention Period</span>
                      <span className="text-white font-semibold mt-1">{log.retentionDays} days</span>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500 block text-xs">Last Rotation</span>
                      <span className="text-white font-semibold mt-1">{log.lastRotation}</span>
                    </div>
                    <div className="bg-slate-700/30 rounded-lg p-3">
                      <span className="text-slate-500 block text-xs">Status</span>
                      <span className={`font-semibold mt-1 ${log.status === 'active' ? 'text-green-400' : 'text-red-400'}`}>
                        {log.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="bg-slate-700/30 rounded-lg p-3">
                    <p className="text-slate-400 text-xs">
                      This log source is configured to retain entries for {log.retentionDays} days before automatic archival or deletion.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditLog(log)}
                    className="p-2 bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 rounded-lg transition-all"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeleteLog(log.id)}
                    className="p-2 bg-red-600/20 text-red-400 hover:bg-red-600/30 rounded-lg transition-all"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingLog ? 'Edit Log Configuration' : 'Create Log Configuration'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1 hover:bg-slate-700 rounded-lg transition-all"
                >
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-300 mb-2">Log Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                    placeholder="e.g., System Access Audit Log"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Data Source</label>
                    <select
                      value={formData.dataSource}
                      onChange={(e) => setFormData({ ...formData, dataSource: e.target.value })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:border-blue-500 focus:outline-none"
                    >
                      <option value="system">System</option>
                      <option value="api">API</option>
                      <option value="security">Security</option>
                      <option value="user">User Activity</option>
                      <option value="database">Database</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-300 mb-2">Retention Period (Days)</label>
                    <input
                      type="number"
                      value={formData.retentionDays}
                      onChange={(e) => setFormData({ ...formData, retentionDays: parseInt(e.target.value) })}
                      className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                      min="1"
                      max="3650"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-semibold transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLog}
                  className="px-6 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white rounded-lg font-semibold transition-all"
                >
                  Save Configuration
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
